/**
 * Messages sent WHILE a turn is running.
 *
 * A v2 turn is one blocking request, and the session may hold only one live turn, so a second
 * `POST /api/sessions/turns` during it is refused (`TURN_BUSY`). What the reader types meanwhile goes
 * to the running turn's INBOX instead (`POST /api/runs/{turn_id}/messages`; a v2 turn is a
 * `multi_agent_runs` row). MSA (leg `v2-run-inbox`) reads the inbox between its steps:
 *
 *   a follow-up to the reply, or a change to the plan being drafted  → taken into that same work;
 *   anything else, or a message that arrived after the last step     → not acted on in this turn.
 *
 * The turn's own result names each message by its inbox ordinal (`TurnOut.detail.follow_ups`:
 * `delivered`, `needs_turn`, `duplicates`). This module holds the rules that turn that answer into
 * what the reader sees and into what the page sends next. It is pure; the session store owns the
 * requests and the order.
 *
 * Every turn on this backend is started by a request, so a message the turn did not act on is sent
 * by the PAGE as the next turn (the same way a card answer continues with `turn_required`). After
 * Stop nothing is sent on the reader's behalf.
 */
import { describeLaneV2Error, isLaneV2Error } from "./api";

export type FollowUpStatus =
  /** The inbox request is in flight. */
  | "sending"
  /** The inbox accepted it; the turn has not said what it did with it yet. */
  | "waiting"
  /** The turn's work took it into account. */
  | "included"
  /** The turn ended and never said what became of it (no result, or a server without the facts). */
  | "unconfirmed"
  /** The server reported it undelivered: the turn ended before it was read (`message_consumed` with hop -1). */
  | "undelivered"
  /** The inbox refused it, or the turn was stopped before it was read. `error` says which. */
  | "failed";

export interface FollowUp {
  localId: string;
  text: string;
  at: string;
  /** The running turn it was sent to. */
  turnId: string;
  /** The inbox's ordinal for it, once accepted; what `detail.follow_ups` names it by. */
  inboxSeq: number | null;
  status: FollowUpStatus;
  error?: string | null;
  /** The context chip or attachments were on screen when this was sent: they do not ride a follow-up and wait for the next message. */
  withoutFile?: boolean;
}

/** The queued message's caption: the inbox took it and the running task will read it. */
export const SENT_TO_TASK_COPY = "Sent to the running task";
/** The server said the turn ended without reading it. */
export const UNDELIVERED_COPY = "Not delivered \u2014 the turn ended";

/** Shown under a follow-up that left the chip / attachments behind. */
export const WITHOUT_FILE_COPY = "Sent without the file. It will go with your next message.";

/** `TurnOut.detail.follow_ups`, by inbox ordinal. */
export interface FollowUpFacts {
  delivered: number[];
  needsTurn: number[];
  duplicates: number[];
}

/** How many messages may wait on one running turn; the next one waits for the reply. */
export const MAX_WAITING_FOLLOW_UPS = 3;

export const STOPPED_COPY = "Stopped before Weave read this. Send it again if you still need it.";

function ordinals(value: unknown): number[] {
  return Array.isArray(value) ? value.filter((n): n is number => typeof n === "number" && Number.isInteger(n)) : [];
}

/** The facts off a turn's `detail`, or null when it carries none (the turn took no message, or an older server). */
export function followUpFactsOf(detail: unknown): FollowUpFacts | null {
  if (!detail || typeof detail !== "object") return null;
  const raw = (detail as Record<string, unknown>).follow_ups;
  if (!raw || typeof raw !== "object") return null;
  const f = raw as Record<string, unknown>;
  return { delivered: ordinals(f.delivered), needsTurn: ordinals(f.needs_turn), duplicates: ordinals(f.duplicates) };
}

/** `message_consumed.hop` for a message the turn ended without reading (MSA `run_inbox.UNDELIVERED_HOP`). */
export const UNDELIVERED_HOP = -1;

const OPEN: ReadonlySet<FollowUpStatus> = new Set(["sending", "waiting", "unconfirmed"]);

/**
 * Settle the messages sent to `turnId` now that its result is known.
 *
 * `sendNext` is what the page must submit as the next turn(s), in sending order: every message the
 * turn did not act on. With no facts at all nothing is sent on a guess (a delivered message would be
 * answered twice); the message is marked instead. After Stop nothing is sent either.
 */
export function settleFollowUps(
  list: readonly FollowUp[],
  turnId: string,
  facts: FollowUpFacts | null,
  opts: { stopped: boolean },
): { followUps: FollowUp[]; sendNext: string[] } {
  const followUps: FollowUp[] = [];
  const sendNext: string[] = [];
  for (const f of list) {
    if (f.turnId !== turnId || !OPEN.has(f.status)) {
      followUps.push(f);
      continue;
    }
    const seq = f.inboxSeq;
    if (seq !== null && facts?.delivered.includes(seq)) {
      followUps.push({ ...f, status: "included", error: null });
    } else if (seq !== null && facts?.duplicates.includes(seq)) {
      // The same words twice: the first copy carries it.
    } else if (opts.stopped) {
      followUps.push({ ...f, status: "failed", error: STOPPED_COPY });
    } else if (!facts) {
      followUps.push({ ...f, status: "unconfirmed" });
    } else {
      sendNext.push(f.text);
    }
  }
  return { followUps, sendNext };
}

/** The turn ended but its result has not arrived (yet, or ever): its open messages are unconfirmed until it does. */
export function unconfirmFollowUps(list: readonly FollowUp[], turnId: string): FollowUp[] {
  return list.map((f) => (f.turnId === turnId && (f.status === "waiting" || f.status === "sending") ? { ...f, status: "unconfirmed" } : f));
}

/**
 * A `message_consumed` report for one message of `turnId`: with `hop === UNDELIVERED_HOP` the turn ended without reading it,
 * so an open row becomes `undelivered` (resendable); any other hop means it was read, so the row is `included`.
 * Matched by the inbox ordinal; a row already settled is left alone.
 */
export function applyMessageConsumed(
  list: readonly FollowUp[],
  turnId: string,
  consumed: { inbox_seq: number; hop: number },
): FollowUp[] {
  return list.map((f) => {
    if (f.turnId !== turnId || f.inboxSeq !== consumed.inbox_seq || !OPEN.has(f.status)) return f;
    return consumed.hop === UNDELIVERED_HOP ? { ...f, status: "undelivered", error: null } : { ...f, status: "included", error: null };
  });
}

/** May the composer send to the running turn's inbox right now? */
export function canSendFollowUp(list: readonly FollowUp[], activeTurnId: string | null): boolean {
  if (!activeTurnId) return false;
  const open = list.filter((f) => f.turnId === activeTurnId && (f.status === "sending" || f.status === "waiting")).length;
  return open < MAX_WAITING_FOLLOW_UPS;
}

/** One line under the message, in plain words. */
export function followUpCaption(f: FollowUp): string {
  switch (f.status) {
    case "sending":
      return "Sending…";
    case "waiting":
      return SENT_TO_TASK_COPY;
    case "included":
      return "Included in the reply above.";
    case "unconfirmed":
      return "Weave did not confirm it read this. Send it again if the reply missed it.";
    case "undelivered":
      return UNDELIVERED_COPY;
    case "failed":
      return f.error || "This was not sent. You can send it again.";
  }
}

/**
 * "That turn has already ended": send the text as a normal turn. The Lane V2 steer route answers 409 `TURN_ENDED`
 * (`error_code`); the old run inbox answered 409 `{detail:{code:"RUN_TERMINAL"}}`. Codes only, never message text.
 */
export function isRunTerminal(err: unknown): boolean {
  if (!isLaneV2Error(err) || err.status !== 409) return false;
  if (err.code === "TURN_ENDED") return true;
  const payload = err.payload;
  if (!payload || typeof payload !== "object") return false;
  const detail = (payload as Record<string, unknown>).detail;
  return !!detail && typeof detail === "object" && (detail as Record<string, unknown>).code === "RUN_TERMINAL";
}

// ── after a reload: the turn's own text carries what was added to it ─────────────────────────────

/**
 * MSA's mark for a message a turn took while it ran (`dialogue.FOLLOW_UP_MARK`). R5's key set is the
 * contract's exactly, so these ride `user_text`, each on its own line behind this mark. Keep in step
 * with MSA; an unknown mark simply shows as the text MSA sent.
 */
export const FOLLOW_UP_MARK = "[sent while the reply was being written]";
const FOLLOW_UP_LABEL = "↳ Added while Weave was working:";

/** R5 `user_text` for display: each added message on its own paragraph, behind a plain label. */
export function readableUserText(text: string | null): string | null {
  if (!text || !text.includes(FOLLOW_UP_MARK)) return text;
  return text
    .split("\n")
    .map((line) => (line.startsWith(FOLLOW_UP_MARK) ? `\n${FOLLOW_UP_LABEL}${line.slice(FOLLOW_UP_MARK.length)}` : line))
    .join("\n");
}

/** The words alone, for sending a shown message again: no label, no blank separator lines added by `readableUserText`. */
export function plainUserText(shown: string): string {
  if (!shown.includes(FOLLOW_UP_LABEL)) return shown;
  return shown
    .split(`\n\n${FOLLOW_UP_LABEL} `)
    .join("\n");
}

export const TURN_GONE_COPY = "This turn is no longer available.";
export const INBOX_BUSY_COPY = "The task is busy \u2014 try sending again in a moment.";
const NOT_SENT_COPY = "This was not sent. You can send it again.";

/** Why a steer message was not sent, by `error_code` only (INBOX_BUSY here means the one retry also failed). */
export function steerFailureCopy(err: unknown): string {
  if (!isLaneV2Error(err) || !err.code) return NOT_SENT_COPY;
  if (err.code === "TURN_NOT_FOUND") return TURN_GONE_COPY;
  if (err.code === "INBOX_BUSY") return INBOX_BUSY_COPY;
  return describeLaneV2Error(err);
}
